/**
 * List slice for the MasterView store.
 *
 * Manages search params, data, loading state, and selection.
 *
 * @internal
 * @module store/slices/listSlice
 */
import type { StateCreator } from 'zustand';
import type { MasterPatternViewEntity } from '../../types/service.types';
import type { MasterViewState, ListState, SearchParams, ActiveFilter } from '../types';

export const DEFAULT_SEARCH_PARAMS: SearchParams = {
  page: 1,
  pageSize: 20,
  search: '',
  sortBy: '',
  sortOrder: 'asc',
  filters: [],
};

export const createListSlice = <T extends MasterPatternViewEntity>(
  initialPageSize?: number,
): StateCreator<
  MasterViewState<T>,
  [],
  [],
  ListState<T>
> => (set) => ({
  searchParams: { ...DEFAULT_SEARCH_PARAMS, ...(initialPageSize ? { pageSize: initialPageSize } : {}) },
  columnFilters: [],
  quickFilterValues: {},
  data: [],
  total: 0,
  isLoading: false,
  selectedIds: [],
  hasSearched: false,

  setSearchParams: (params: Partial<SearchParams>) =>
    set((state) => ({
      searchParams: { ...state.searchParams, ...params },
    })),

  setColumnFilters: (filters: ActiveFilter[]) =>
    set({ columnFilters: filters }),

  clearColumnFilters: () =>
    set({ columnFilters: [] }),

  setQuickFilterValues: (values: Record<string, string>) =>
    set({ quickFilterValues: values }),

  clearQuickFilters: () =>
    set({ quickFilterValues: {} }),

  setData: (data: T[], total: number) =>
    set({ data, total }),

  setIsLoading: (isLoading: boolean) =>
    set({ isLoading }),

  toggleSelection: (id: string) =>
    set((state) => ({
      selectedIds: state.selectedIds.includes(id)
        ? state.selectedIds.filter((selectedId) => selectedId !== id)
        : [...state.selectedIds, id],
    })),

  clearSelection: () =>
    set({ selectedIds: [] }),

  setHasSearched: (hasSearched: boolean) =>
    set({ hasSearched }),
});
